# Introduction

## Shape the way users and agents use your MCP server.

Taias is a TypeScript library for builders who want their ChatGPT App / MCP App / MCP Server to be a cohesive product experience that delivers outcomes. Agents and users decide what to do next, but you can guide them.

<Callout>
  If you're using these docs in an LLM, access [llms-full.txt](/llms-full.txt)
  for the complete documentation context to get started quickly.
</Callout>

---

## Why Taias Exists

When an agent uses your MCP server, each tool call is isolated. The agent has context from the conversation and sees your tool descriptions, but it doesn't know what makes sense to do next from a _product_ perspective.

**Example:** A user asks to see their recently played tracks. Your MCP server returns the list - maybe with some UI buttons. What happens next?

The agent might suggest creating a playlist, or the user might ask something unrelated, or the conversation might just end. There's no product layer saying "users who check their recent tracks usually want to discover new music or create playlists."

**In traditional apps**, you designed the entire screen. After showing recent tracks, you'd surface personalized recommendations right there, or prompt playlist creation, or show social features. The next step was _designed_, not left to chance.

**In MCP experiences**, you return tool results and UI components, but coordinating what comes next across multiple tool calls is manual and scattered. You might hint at next steps in your tool descriptions, mention usage patterns in server instructions, or rely on the UI buttons you include, but there's no systematic way to express "these tools form a journey."

This puts you in a **reactive position** - hoping the agent or user happens to request the next meaningful action.

**The gap:** You need a lightweight way to be **proactive** about product intent - to say "after this tool, consider suggesting that tool" - without hardcoding behavior or building complex orchestration.

**What Taias provides:**

A simple organization layer for expressing multi-step flows. You define the connections between tools, and Taias generates guidance that nudges agents and users toward next steps.

Agents and users still make the final call, but you're shaping the experience rather than reacting to it.

---

## How It Works

Define flows with explicit logic statements:

```ts
export const musicDiscoveryFlow = defineFlow("music_discovery_flow", (flow) => {
  flow.step(
    { toolName: { is: "get_recent_tracks" } },
    { nextTool: "get_recommendations" },
  );
  flow.step(
    { toolName: { is: "get_recommendations" } },
    { nextTool: "create_playlist" },
  );
});

export const taias = createTaias({ flow: musicDiscoveryFlow });
```

Each `flow.step()` is a logic statement: a match condition (the conditions under which this step applies) paired with a decision (what should happen next). Match conditions use explicit operators (`{ is: ... }`, `{ isNot: ... }`) and can match on `toolName`, `params` (tool input parameters), and `result` (tool execution output). All fields are optional -- steps can match on any combination. See [Match Conditions](/docs#flows-and-decisions) for details.

Then resolve affordances in your tool responses:

```ts
const affordances = await taias.resolve({
  toolName: "get_recent_tracks",
  params: input,
  result: { trackCount: tracks.length },
});

let message = `Found ${tracks.length} recently played tracks.`;

if (affordances?.advice) {
  message += `\n\n${affordances.advice}`;
}

return { content: [{ type: "text", text: message }] };
```

Taias auto-generates guidance that influences the agent's next action - keeping users on the path toward outcomes you've designed.

---

## Beyond LLM Hints: UI Affordances

LLM advice nudges the agent, but what about the buttons and UI components in your widgets?

Taias can also select **UI affordances** - determining which CTAs, variants, or components appear based on your flow. Register what UI options exist, and Taias automatically selects the right one when the flow decision changes.

```ts
const trackListAffordances = defineAffordances((r) => {
  r.primaryCta("tracks.cta.recommend", { toolName: "get_recommendations" });
  r.primaryCta("tracks.cta.playlist", { toolName: "create_playlist" });
});

export const taias = createTaias({
  flow: musicDiscoveryFlow,
  affordances: mergeAffordances([trackListAffordances]),
});
```

When you call `resolve()`, you get both `advice` for the LLM and `selections` for your widgets:

```ts
const { advice, selections } = await taias.resolve({
  toolName: "get_recent_tracks",
});

// selections.primaryCta.handleId === "tracks.cta.recommend"
```

Your widget renders by `handleId` - and when you change your flow, the UI updates automatically.

See [UI Affordances](/docs#ui-affordances) for the full guide.
